// src/components/PasswordReset.js
import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  LinearProgress,
  Box,
  InputAdornment,
  Link,
  Fade,
  IconButton,
} from '@mui/material';
import {
  Email,
  ArrowBack,
  LockReset,
  Send,
} from '@mui/icons-material';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/Config';
import { useNavigate } from 'react-router-dom';

const PasswordReset = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({ email: '' });
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  // Função de validação
  const validateForm = () => {
    const newErrors = { email: '' };
    let isValid = true;

    if (!email.trim()) {
      newErrors.email = "O email é obrigatório";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Digite um email válido";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Função para traduzir erros do Firebase
  const getFirebaseErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-email': 'Email inválido',
      'auth/user-not-found': 'Não existe uma conta com este email',
      'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde',
      'auth/network-request-failed': 'Erro de conexão. Verifique sua internet',
      'auth/user-disabled': 'Esta conta foi desativada',
    };
    return errorMessages[errorCode] || 'Erro ao enviar email. Tente novamente';
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        text: "Por favor, corrija os erros no formulário",
        type: "error"
      });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      await sendPasswordResetEmail(auth, email);
      setEmailSent(true);
      setMessage({
        text: 'Link de recuperação enviado! Verifique seu email.',
        type: 'success'
      });
    } catch (err) {
      console.error("Erro ao enviar email:", err);
      const errorMessage = getFirebaseErrorMessage(err.code);
      setMessage({ text: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setMessage({ text: '', type: '' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
      }}
    >
      <Fade in={true} timeout={800}>
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 3, sm: 5 },
            width: '100%',
            maxWidth: 480,
            borderRadius: 4,
            background: '#ffffff',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          {/* Botão Voltar */}
          <Box sx={{ mb: 2 }}>
            <IconButton
              onClick={() => navigate('/login')}
              sx={{
                color: '#667eea',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
          </Box>

          {/* Título e Descrição */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <LockReset
                sx={{
                  fontSize: 48,
                  color: '#667eea',
                  mr: 1,
                }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Recuperar Senha
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {emailSent
                ? 'Email enviado com sucesso! Verifique sua caixa de entrada.'
                : 'Digite seu email para receber o link de recuperação de senha.'}
            </Typography>
          </Box>

          {/* Indicador de Progresso */}
          {loading && (
            <LinearProgress
              sx={{
                mb: 3,
                borderRadius: 1,
                height: 6,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                },
              }}
            />
          )}

          {!emailSent ? (
            <form onSubmit={handlePasswordReset}>
              <TextField
                label="Email"
                placeholder="seu.email@exemplo.com"
                variant="outlined"
                fullWidth
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ email: '' });
                }}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                startIcon={<Send />}
                sx={{
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: '#cccccc',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? 'Enviando...' : 'Enviar Link de Recuperação'}
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Lembrou sua senha?
                </Typography>
                <Link
                  href="/login"
                  underline="hover"
                  sx={{
                    color: '#667eea',
                    fontWeight: 600,
                    '&:hover': {
                      color: '#764ba2',
                    },
                  }}
                >
                  Voltar para o Login
                </Link>
              </Box>
            </form>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 4px 20px rgba(72, 187, 120, 0.4)',
                }}
              >
                <Send sx={{ fontSize: 40, color: '#fff' }} />
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#48bb78' }}>
                Email Enviado!
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                Se não encontrar o email, verifique também a pasta de spam.
              </Typography>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                sx={{
                  mb: 2,
                  py: 1.2,
                  borderColor: '#667eea',
                  color: '#667eea',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#764ba2',
                    backgroundColor: 'rgba(102, 126, 234, 0.05)',
                  },
                }}
              >
                Enviar Novamente
              </Button>

              <Link
                href="/login"
                underline="none"
                sx={{ display: 'block', width: '100%' }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    },
                  }}
                >
                  Ir para o Login
                </Button>
              </Link>
            </Box>
          )}

          {/* Informações de Segurança */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid #e0e0e0',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              🔒 Link válido por 1 hora
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Em caso de problemas, entre em contato com o suporte
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Snackbar para Mensagens */}
      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={message.type}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {message.text}
        </Alert>
      </Snackbar>

      {/* Copyright */}
      <Typography
        variant="body2"
        sx={{
          position: 'fixed',
          bottom: 16,
          width: '100%',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.8)',
          fontWeight: 500,
        }}
      >
        © 2025 AAP-GIS. Todos os direitos reservados.
      </Typography>
    </Box>
  );
};

export default PasswordReset;
